<script lang="ts" context="module">
  import { cva, type VariantProps } from 'class-variance-authority';
  import { aboveLg } from '$lib/stores/breakpoints';
  import ProductBadges from '../ui/product-badges.svelte';
  // Style
  type CardVariant = VariantProps<typeof styles.host>['variant'];

  const DESKTOP_ALT = 'desktop-only' as const;

  const styles = {
    host: cva('flex flex-col relative isolate rounded-md', {
      variants: {
        variant: {
          default: 'aspect-[183/304] lg:aspect-[332/480]',
          highlight: 'aspect-[332/480]',
          small: 'aspect-[183/304]',
          'gift-card': 'aspect-[228/344]'
        },
        isAddToCardDrawerOpen: {
          false: 'group'
        },
        selectedGiftCard: {
          true: 'after:absolute after:inset-[-1px] after:rounded-md after:border after:border-[--freeGiftAltTextColor] cursor-pointer',
          black:
            'after:absolute after:inset-[-1px] after:rounded-md after:border after:border-accent cursor-pointer'
        }
      }
    }),
    buttonWrapper: cva('', {
      variants: {
        hasTieredCampaign: {
          true: 'items-center gap-2.5'
        },
        variant: {
          default: '',
          highlight: '',
          small: '',
          'gift-card': ''
        }
      },
      compoundVariants: [
        {
          hasTieredCampaign: true,
          variant: 'highlight',
          class: 'flex p-0.5 pl-2.5 rounded-sm bg-[var(--tieredAltBackgroundColor)]'
        },
        {
          hasTieredCampaign: true,
          variant: 'default',
          class: 'lg:flex lg:p-0.5 lg:pl-2.5 lg:rounded-sm lg:bg-[var(--tieredAltBackgroundColor)]'
        }
      ]
    }),
    title: cva(
      'focus-visible:before:ring before:shadow-none before:transition-shadow before:rounded-sm border-none outline-none shadow-none motion-safe:transition-shadow rounded-xs',
      {
        variants: {
          variant: {
            default: 'text-sm lg:text-md !font-semibold',
            highlight: 'text-md font-semibold',
            small: 'text-sm font-semibold',
            'gift-card': 'cursor-pointer text-sm lg:text-md !font-semibold'
          }
        }
      }
    ),
    category: cva('text-primary/40', {
      variants: {
        variant: {
          default: 'text-xs lg:text-sm',
          highlight: 'text-sm',
          small: 'text-xs',
          'gift-card': 'text-sm lg:text-md'
        }
      }
    }),
    priceWrapper: cva('px-0.5 flex', {
      variants: {
        variant: {
          default: 'gap-0.5 lg:gap-1',
          highlight: 'gap-1',
          small: 'gap-0.5',
          'gift-card': ''
        }
      }
    }),
    price: cva('whitespace-nowrap', {
      variants: {
        variant: {
          default: 'text-sm text-primary font-semibold lg:text-md lg:font-semibold',
          highlight: 'text-md font-semibold',
          small: 'text-sm font-semibold',
          'gift-card': ''
        }
      }
    }),
    infoWrapper: cva('bg-highlight rounded-b-md', {
      variants: {
        variant: {
          default: 'p-1.5 lg:p-2',
          highlight: 'p-2',
          small: 'p-1.5',
          'gift-card': 'p-2.5'
        },
        showOverflow: {
          true: 'relative z-0 overflow-hidden'
        }
      }
    }),
    saleBanner: cva(
      'text-[0.6875rem] leading-normal tracking-tight font-medium bg-[var(--tieredAltBackgroundColor)] text-[var(--tieredAltTextColor)] p-1 text-center mb-1 rounded-[6px]',
      {
        variants: {
          variant: {
            default: 'lg:hidden',
            highlight: 'hidden',
            small: '',
            'gift-card': 'hidden'
          },
          defaultPallette: {
            true: 'text-primary bg-primary/8'
          }
        }
      }
    )
  } as const;

  /* GraphQL */ `
    query getMetaObjectsbyIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        __typename
        ... on Metaobject {
          id
          fields {
            key
            value
          }
        }
      }
    }
  `;

  // Consts
  const OPTIMAL_WIDTH = 700 as const;

  // Utils
  export const parseColorSelectorMetafield = (
    product: ProductCardFragment | QuickAddToCartFragment
  ) => {
    const metafield = product.metafields?.find((p) => p?.key === ProductMetafields.VARIANT_COLOR);
    if (
      product.metafields?.find((p) => p?.key === ProductMetafields.VARIANT_IMAGES) &&
      metafield &&
      Array.isArray(JSON.parse(metafield.value)) &&
      !!JSON.parse(metafield.value).length
    ) {
      return metafield;
    }
  };

  const parsePatternMetafield = (product: ProductCardFragment) => {
    const metafield = product.metafields.find((p) => p?.key === ProductMetafields.CARD_PATTERN);
    if (metafield && metafield.value) {
      return metafield;
    }
  };

  export const flatOptions = (options: ProductCardFragment['options']) =>
    options?.map((opt) => opt.values.map((val) => val)).flat();

  const getVariantOptions = ({
    options,
    images
  }: {
    options: ProductCardFragment['options'];
    images: ShopifyImage[];
  }) => {
    const allOptions = flatOptions(options);

    if (allOptions) {
      return [
        ...new Set([
          ...images
            .filter((img) => img.altText && allOptions.includes(img.altText))
            .map((img) => img.altText)
        ])
      ].filter((img) => !!img);
    } else return;
  };

  const getSrc = (sources: ShopifyVideo['sources']) => {
    const lowQuality = sources.find((src) => src.url.includes('SD'))?.url;
    return lowQuality;
  };
</script>

<script lang="ts">
  import Icon from '$components/ui/icon/icon.svelte';
  import Gallery from './gallery.svelte';
  import AddToCart from './add-to-cart.svelte';
  import ShopifyImg from '$components/shopify-img.svelte';
  import ColorSelector from '$components/color-selector.svelte';
  import WishlistButton from '$components/wishlist-button.svelte';
  import ProductCardVideo from './product-card-video.svelte';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { page } from '$app/stores';
  import { buildPath } from '$lib/utils/paths';
  import { buildSizes } from '$lib/image';
  import { formatCurrency } from '$lib/utils/currency';
  import {
    categoryCampaigns,
    freeGiftCampaign,
    getCampaignColors,
    tieredCampaign,
    tieredCampaignColors
  } from '$lib/stores/campaigns';
  import { getGalleryPhotos } from '$lib/utils/shopify/getGalleryPhotos';
  import { DEFAULT_CAMPAIGN_COLOR, FREE_GIFT, ProductMetafields } from '$lib/constants';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { splitTitleAndCategory } from '$lib/utils/shopify/splitTitleAndCategory';

  import type { Action } from 'svelte/action';
  import type { ShopifyImage, ShopifyVideo } from '$types/shopify';
  import type { ProductCardFragment, QuickAddToCartFragment } from '$gql/storefront/operations';
  import { getVariantId, trimVariantId } from '$lib/utils/shopify/productsId';
  import { selectedGift } from '$components/cart/change-gift.svelte';
  import { createEventDispatcher } from 'svelte';
  import { cart } from '$lib/cart';
  import { region } from '$lib/region';

  export let product: ProductCardFragment;
  export let variant: CardVariant = 'default';
  let selectedVariantId: string | undefined = undefined;
  let className: string | undefined = undefined;
  export { className as class };

  const dispatch = createEventDispatcher<{ handleSelectGift: undefined }>();

  const selectGift = () => {
    const freeProduct = $cart?.lines.nodes.find((line) =>
      (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
    );
    let variantId: string | undefined = undefined;

    if (product.handle === freeProduct?.merchandise.product.handle) {
      variantId = trimVariantId(freeProduct?.merchandise?.id as string);
    } else {
      const variantNode = selectedVariantOption
        ? product.variants.nodes.find((item) => item.title === selectedVariantOption)
        : product.variants.nodes[0];
      variantId = trimVariantId(variantNode?.id as string);
    }

    selectedGift.update((curr) => {
      return {
        ...curr,
        handle: product.handle,
        variantId
      };
    });

    dispatch('handleSelectGift');
  };

  // Data parse
  $: ({ title, category } = splitTitleAndCategory({
    title: product.title,
    titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
  }));
  $: productId = product.id;
  $: currency = product.priceRange.minVariantPrice.currencyCode;
  $: options = product.options;
  $: images =
    variant === 'small'
      ? [product.images.nodes[0]]
      : getGalleryPhotos(
          product.images.nodes,
          variant === 'highlight' ? 'highlight' : variantColor ? 'all' : 'default'
        );

  $: videosInProductCard = $page.data.configuration.content.videos_in_product_cards?.find(
    (entry) =>
      entry.regions?.includes($region) &&
      entry.product_cards?.some((card) => card.slug === product.handle)
  );

  $: videos =
    videosInProductCard != null
      ? (product.media.nodes || []).filter(
          (item) =>
            (item.__typename === 'Video' && item.alt && item.alt === DESKTOP_ALT && $aboveLg) ||
            (item.__typename === 'Video' && !item.alt && !$aboveLg)
        )
      : [];

  $: pickedVideoSrc =
    videos.length > 0 && videos[0].__typename === 'Video' ? getSrc(videos[0].sources) : null;

  $: isGiftCard = variant === 'gift-card';

  $: selectedOutlineCallout = $page.data.configuration.content.outline_callout_titles?.find(
    (callout) => {
      const tags = (callout.tags || '').split('\n');
      return tags?.some((tag) => product.tags.includes(tag));
    }
  );

  $: showOutline = selectedOutlineCallout != null && selectedOutlineCallout?.product_card != '';
  $: outlineBackgroudCampaignColors =
    showOutline && selectedOutlineCallout?.colors
      ? getCampaignColors(selectedOutlineCallout.colors)
      : null;

  let derivedSearchQuery: string | undefined = undefined;

  $: {
    if (selectedVariantOption) {
      const variantNode = product.variants.nodes.find(
        (item) => item.title === selectedVariantOption
      );

      selectedVariantId = variantNode?.id;

      if (variantNode) {
        derivedSearchQuery = getVariantId(variantNode.id);
      }
    }
  }

  $: href = $buildPath({ path: `/products/${product.handle}`, searchQuery: derivedSearchQuery });

  // Color selector metafield condition
  $: variantColor = parseColorSelectorMetafield(product);

  // Sale conditions
  $: isOnSale = !!(comparePrice && +comparePrice > +price);
  $: hasTieredCampaign = !!$tieredCampaign && !$tieredCampaign.isProductExcluded(product);
  $: hasCategoryCampaign =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;
  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(product)
  );

  // Price
  $: price = product.priceRange.minVariantPrice.amount;
  $: comparePrice = product.compareAtPriceRange.minVariantPrice.amount;

  // Gallery nav
  let next: () => void;
  let prev: () => void;
  const galleryKeyboardNav: Action = (node: HTMLElement) => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prev && !isAddToCardDrawerOpen) prev();
      if (e.key === 'ArrowRight' && next && !isAddToCardDrawerOpen) next();
    };
    node.addEventListener('keydown', handler);
    return {
      destroy: () => node.removeEventListener('keydown', handler)
    };
  };

  // Image variant
  let imagesByVariant: Map<string, ShopifyImage[]> | undefined = undefined;
  $: imageVariantOptions = getVariantOptions({ images, options });
  let selectedVariantOption = (() => {
    if (parseColorSelectorMetafield(product)) {
      // get first available variant
      const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
      if (availableVariant) {
        selectedVariantId = availableVariant.id;
        return flatOptions(product.options).find((option) => option === availableVariant.title);
      }
      return flatOptions(product.options)[0];
    }
  })();

  $: if (!!variantColor && !!options.length && imageVariantOptions) {
    const tmp = new Map<string, ShopifyImage[]>();
    imageVariantOptions.forEach((variant) => {
      if (!variant) return;
      const imgs = images.filter((img) => img.altText === variant);
      tmp.set(variant, imgs);
    });
    imagesByVariant = tmp;
  }

  $: parsedImages =
    !!imagesByVariant && !!selectedVariantOption && variant !== 'small'
      ? imagesByVariant.get(selectedVariantOption)
      : images;

  $: shouldImagesCover = product.metafields?.some(
    (p) => p?.key === ProductMetafields.CARD_OBJECT_FIT && p?.value === 'true'
  );

  let active = true;
  let info: HTMLDivElement;
  $: infoH = info?.clientHeight || 90;
  let isAddToCardDrawerOpen = false;

  $: activeTieredColor = $tieredCampaignColors.activeTieredColor;
  $: tieredAltTextColor = $tieredCampaignColors.tieredAltTextColor;
  $: tieredAltBackgroundColor = $tieredCampaignColors.tieredAltBackgroundColor;

  $: freeGiftCampaignColors = $freeGiftCampaign?.campaign_color
    ? getCampaignColors($freeGiftCampaign?.campaign_color)
    : null;

  let isBlackCampaignColor: boolean;
  let categoryAltTextColor: string | undefined = undefined;
  let categoryAltBackgroundColor: string | undefined = undefined;

  $: if (categoryCampaign?.campaign_color) {
    const categoryCampaignColors = getCampaignColors(categoryCampaign.campaign_color);
    isBlackCampaignColor = categoryCampaignColors.isBlackColor;
    categoryAltTextColor = categoryCampaignColors.altTextColor;
    categoryAltBackgroundColor = categoryCampaignColors.altBackgroundColor;
  }

  $: metafield = parsePatternMetafield(product);

  $: todaysFreeGifts = $freeGiftCampaign?.campaign_free_gifts
    ?.filter((p) => typeof p !== 'string')
    .map((p) => p.slug);

  $: isFreeGift =
    (todaysFreeGifts || []).includes(product.handle) &&
    $freeGiftCampaign?.free_gifts_cannot_be_bought;
</script>

<div
  use:galleryKeyboardNav
  use:intersectionObserver={{
    options: {
      // trigger visibility before entering the screen
      rootMargin: '100px 0px 100px 0px'
    },
    callback: (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          active = true;
        } else {
          active = false;
        }
      });
    }
  }}
  class={cn(
    styles.host({
      variant,
      isAddToCardDrawerOpen,
      selectedGiftCard:
        isGiftCard &&
        $selectedGift?.handle === product.handle &&
        freeGiftCampaignColors?.isBlackColor
          ? 'black'
          : isGiftCard && $selectedGift?.handle === product.handle
    }),
    'product-card',
    className,
    'host',
    variant,
    'elevated-links',
    showOutline &&
      !isGiftCard &&
      (outlineBackgroudCampaignColors?.isBlackColor
        ? 'border border-accent bg-accent'
        : 'border border-[--outlineBackgroudColor] bg-[--outlineBackgroudColor]')
  )}
  style:--info-h={infoH + 'px'}
  style:--tieredAltBackgroundColor={tieredAltBackgroundColor}
  style:--tieredAltTextColor={tieredAltTextColor}
  style:--outlineBackgroudColor={outlineBackgroudCampaignColors?.altTextColor}
  style:--categoryAltBackgroundColor={categoryAltBackgroundColor}
  style:--categoryAltTextColor={categoryAltTextColor}
  style:--outlineHeaderHeight={showOutline ? '20px' : '0px'}
  style:--freeGiftAltBackgroundColor={freeGiftCampaignColors?.altBackgroundColor}
  style:--freeGiftAltTextColor={freeGiftCampaignColors?.altTextColor}
>
  {#if showOutline && !isGiftCard}
    <p class="py-1 px-1 text-center text-highlight text-xs font-medium">
      {selectedOutlineCallout?.product_card}
    </p>
  {/if}

  {#if isGiftCard}
    <button
      class={cn(
        'absolute top-2.5 z-50 rounded-full w-4 h-4 right-2.5 border border-primary/40 after:absolute after:top-0.5 after:right-0.5 after:rounded-full after:w-2.5 after:h-2.5',
        $selectedGift?.handle === product.handle &&
          'border-[--freeGiftAltTextColor] after:bg-[--freeGiftAltTextColor]',
        $selectedGift?.handle === product.handle &&
          freeGiftCampaignColors?.isBlackColor &&
          'border-accent after:bg-accent'
      )}
      on:click={() => selectGift()}
    ></button>
  {/if}

  <div class=" grow isolation-auto z-10 relative">
    <Gallery
      let:itemClass
      bind:next
      bind:prev
      on:handleClick={isGiftCard ? () => selectGift() : undefined}
      {active}
      href={isGiftCard ? undefined : href}
      total={(parsedImages || []).length + (videos || []).length}
      class={cn('rounded-b-[unset] h-full', isGiftCard && 'cursor-pointer')}
    >
      {#each parsedImages || [] as { url: src, altText: alt }, i}
        {#if pickedVideoSrc && i === Number(videosInProductCard?.video_position) - 1}
          <ProductCardVideo src={pickedVideoSrc} {itemClass} {active} />
        {/if}
        <div class="relative min-w-full h-full overflow-hidden bg-repeat">
          <ShopifyImg
            {src}
            {alt}
            widths={[OPTIMAL_WIDTH, (w) => (w < OPTIMAL_WIDTH ? w : null)]}
            sizes={buildSizes({
              default: `${OPTIMAL_WIDTH}px`,
              lg: variant === 'highlight' ? '100vw' : '50vw'
            })}
            draggable="false"
            class={cn(
              'select-none',
              itemClass,
              !active && 'hidden',
              'absolute inset-0',
              shouldImagesCover
                ? 'object-cover'
                : ((i === 0 && variant !== 'highlight') || (i === 1 && variant === 'highlight')) &&
                    'object-contain'
            )}
          />
        </div>
      {/each}
      {#if pickedVideoSrc && Number(videosInProductCard?.video_position) > (parsedImages?.length || 0)}
        <ProductCardVideo src={pickedVideoSrc} {itemClass} {active} />
      {/if}
    </Gallery>
  </div>

  {#if !isGiftCard}
    <ProductBadges
      {product}
      stylesClass={'absolute top-[calc(var(--product-card-inset)+var(--outlineHeaderHeight))] left-[--product-card-inset] z-10 pr-6'}
    />
  {/if}

  <div
    class={cn(styles.infoWrapper({ variant, showOverflow: !isAddToCardDrawerOpen }))}
    bind:this={info}
  >
    {#if metafield?.value && !isAddToCardDrawerOpen}
      <img
        src={metafield?.value}
        alt=""
        width="402"
        height="384"
        class="absolute z-[-1] max-w-[402px] h-auto top-1/2 left-1/2"
        style="transform: translate(-50%, -50%)"
      />
    {/if}

    {#if isFreeGift && !isGiftCard}
      <div
        class={cn(
          'bg-[var(--freeGiftAltBackgroundColor)] text-[var(--freeGiftAltTextColor)] p-1 justify-center mb-1 rounded-[6px] gap-1 flex',
          variant === 'default' && 'lg:hidden'
        )}
      >
        <Icon icon="gift" class="w-3 h-3" />
        <p class={cn('text-[0.6875rem] leading-normal tracking-tight font-medium ')}>
          {$t('free-gift.gift-with-purchase')}
        </p>
      </div>
    {/if}

    {#if hasTieredCampaign && $tieredCampaign && !isGiftCard && !isFreeGift}
      <p
        class={cn(
          styles.saleBanner({
            variant,
            defaultPallette:
              hasTieredCampaign && $tieredCampaign && activeTieredColor === DEFAULT_CAMPAIGN_COLOR
          })
        )}
      >
        {$t('product-card.get-for', {
          price: $formatCurrency(currency, $tieredCampaign.getDiscountedPrice(+price)),
          discount: $tieredCampaign.discount
        })}
      </p>
    {/if}

    <div class={cn('mb-1', isGiftCard && 'flex items-center justify-between')}>
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div>
        <svelte:element
          this={isGiftCard ? 'p' : 'a'}
          data-sveltekit-noscroll={false}
          aria-label={$t('a11y.product-name')}
          class={cn(styles.title({ variant }), 'pt-0.5 elevated-link')}
          {href}
          tabindex={-1}
          on:click={isGiftCard ? () => selectGift() : undefined}
        >
          {title}
        </svelte:element>
        <p aria-label={$t('a11y.category')} class={cn(styles.category({ variant }))}>
          {category}
        </p>
      </div>
      {#if isGiftCard}
        <div class="text-right">
          <p class="text-primary font-semibold">{$t('amount.free')}</p>
          <p class="text-primary/40 line-through">{$formatCurrency(currency, +price)}</p>
        </div>
      {/if}
    </div>

    {#if !isGiftCard}
      <div class="flex justify-between items-end">
        <div class={cn(styles.priceWrapper({ variant }))}>
          <p
            class={cn(
              styles.price({ variant }),
              hasCategoryCampaign && 'text-[var(--categoryAltTextColor)]',
              hasCategoryCampaign && isBlackCampaignColor && 'text-accent',
              isFreeGift && 'line-through'
            )}
          >
            {$formatCurrency(
              currency,
              hasCategoryCampaign && categoryCampaign
                ? categoryCampaign?.getDiscountedPrice(+price)
                : +price
            )}
          </p>
          {#if isOnSale && !hasCategoryCampaign}
            <p class={cn(styles.price({ variant }), 'text-primary/40 line-through')}>
              {$formatCurrency(currency, +comparePrice)}
            </p>
          {/if}
          {#if hasCategoryCampaign && categoryCampaign}
            <p class={cn(styles.price({ variant }), 'text-primary/40 line-through')}>
              {$formatCurrency(currency, +price)}
            </p>
          {/if}
        </div>

        {#if isFreeGift && !isGiftCard}
          <div
            class={cn(
              'justify-center items-center bg-[var(--freeGiftAltBackgroundColor)] p-2.5 rounded-xs gap-1 text-[var(--freeGiftAltTextColor)] text-sm',
              freeGiftCampaignColors?.isBlackColor && 'bg-accent text-highlight',

              variant === 'default' && 'lg:flex hidden',
              variant === 'small' && 'hidden'
            )}
          >
            <Icon icon="gift" class="w-4 h-4" />
            <p class="text-sm">{$t('free-gift.gift-with-purchase')}</p>
          </div>
        {:else}
          <div
            class={cn(
              styles.buttonWrapper({
                hasTieredCampaign,
                variant
              })
            )}
          >
            {#if hasTieredCampaign && $tieredCampaign}
              <p
                class={cn(
                  'text-[var(--tieredAltTextColor)] text-sm',
                  variant === 'default' && 'lg:inline hidden',
                  variant === 'small' && 'hidden'
                )}
              >
                {$t('product-card.get-for', {
                  price: $formatCurrency(currency, $tieredCampaign.getDiscountedPrice(+price)),
                  discount: $tieredCampaign.discount
                })}
              </p>
            {/if}
            <AddToCart
              {product}
              class="relative z-10"
              colorSelectorSelectedVariant={variant === 'small' ? undefined : selectedVariantOption}
              bind:open={isAddToCardDrawerOpen}
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>

  {#if !isGiftCard}
    <WishlistButton
      class="absolute z-10 top-[--outlineHeaderHeight] right-0"
      {productId}
      {selectedVariantId}
      variant="filled-icon"
    />
  {/if}

  {#if variantColor?.value && !!options?.length && variant !== 'small' && !isAddToCardDrawerOpen && !isGiftCard}
    <ColorSelector
      bind:selected={selectedVariantOption}
      metafield={variantColor.value}
      class="absolute bottom-[calc(var(--product-card-inset)+var(--info-h))] right-[--product-card-inset] max-h-[65%]"
    />
  {/if}
</div>

<style lang="postcss">
  .host.default {
    --product-card-inset: theme('spacing[1.5]');
    @media (min-width: theme('screens.lg')) {
      --product-card-inset: theme('spacing.2');
    }
  }
  .host.highlight {
    --product-card-inset: theme('spacing.2');
  }
  .host.small {
    --product-card-inset: theme('spacing[1.5]');
  }
</style>
